import type React from 'react'
import { useCallback, useEffect, useImperativeHandle, useReducer, useRef } from 'react'
import type { PropertiesPanelSectionId } from '@/lib/panels/types/properties-panel-section-id'
import type { ShapeType } from '@/lib/shapes/types/shape-type'
import type { ExpandAndOpenTypeOptions, LeftPanelRef } from '../types/left-panel-ref'

interface UseLeftPanelImperativeHandleInput {
  activeItem: string
  activateTool: (item: string) => void
  /** The parent's setter only (not the local fallback) — `expandAndOpenObject` clears through it. */
  propSetSelectedShape?: (shape: ShapeType | null) => void
  resetShapeCreation: () => void
  openSelectToolSectionRaw: (id: PropertiesPanelSectionId) => void
  /** Display-only collapse for `handleRightPanelOpened` — never clears saved section state. */
  collapsePropertiesPanelSectionsTemporarily: () => void
  revealPanel: () => void
  hidePanel: () => void
  selectToolButtonRef: React.RefObject<HTMLButtonElement | null>
  typeToolButtonRef: React.RefObject<HTMLButtonElement | null>
}

/** `LeftPanelRef`: the methods `home.tsx` calls to open, focus and reset the left panel. */
export function useLeftPanelImperativeHandle(
  ref: React.ForwardedRef<LeftPanelRef>,
  {
    activeItem,
    activateTool,
    propSetSelectedShape,
    resetShapeCreation,
    openSelectToolSectionRaw,
    collapsePropertiesPanelSectionsTemporarily,
    revealPanel,
    hidePanel,
    selectToolButtonRef,
    typeToolButtonRef,
  }: UseLeftPanelImperativeHandleInput
) {
  const pendingRightPanelCollapseRef = useRef(false)
  const pendingSectionOpensRef = useRef<PropertiesPanelSectionId[]>([])
  const [, requestDeferredWorkRender] = useReducer((n: number) => n + 1, 0)

  // The section opens below accompany a selection change (a text frame entering edit, a shape
  // click, convert-to-path, ...). ShapeManager's selection changes first and the panel's
  // selection mirror only catches up a commit later, so opening right away showed the section
  // under the *previous* selection for one commit, and the per-selection memory saved it as that
  // selection's own state (e.g. a photo, left by editing a text frame, later reopened with Type).
  // Deferring to the effect below applies the open once the new selection has synced.
  const openSectionAfterSelectionSync = useCallback((id: PropertiesPanelSectionId) => {
    pendingSectionOpensRef.current.push(id)
    requestDeferredWorkRender()
  }, [])

  // Consumes `openSectionAfterSelectionSync`'s queue, then the flag `handleRightPanelOpened`
  // sets: collapsing here, after the tool-switch render has fully committed (and any downstream
  // effects it triggered, e.g. finalizing an in-progress shape-text edit, have run), means
  // nothing started by leaving Type/Shape can reopen a section behind this — this collapse
  // always has the last word.
  useEffect(() => {
    if (pendingSectionOpensRef.current.length > 0) {
      const ids = pendingSectionOpensRef.current

      pendingSectionOpensRef.current = []
      ids.forEach(openSelectToolSectionRaw)
    }

    if (pendingRightPanelCollapseRef.current) {
      pendingRightPanelCollapseRef.current = false
      collapsePropertiesPanelSectionsTemporarily()
    }
  })

  useImperativeHandle(
    ref,
    () => ({
      revealSelectTool: () => {
        propSetSelectedShape?.(null)
        activateTool('select')
        revealPanel()
      },
      hideSelectToolPanel: () => {
        // Only the Select tool's panel is about the selection; Type, Shapes, Document and Spell
        // Check stay open when the selection empties.
        if (activeItem === 'select') hidePanel()
      },
      expandAndOpenObject: () => {
        propSetSelectedShape?.(null)
        activateTool('select')
        // Raw, not the scroll-notifying wrapper: `home.tsx` calls this (convert-to-path, the
        // right-panel Escape fallback) to guarantee Object is open as a fallback — it's not a
        // deliberate "scroll to Object" gesture, and always fires
        // alongside a selection change that must drive the scroll instead (see
        // `usePropertiesPanelScroll`/`openSelectToolSection`'s doc comment).
        openSectionAfterSelectionSync('object')
        revealPanel()
      },
      expandAndOpenType: (opts?: ExpandAndOpenTypeOptions) => {
        resetShapeCreation()
        revealPanel()

        if (activeItem === 'select') {
          // Raw for the same reason as `expandAndOpenObject` above — this fires alongside a
          // text selection/edit change driven by `home.tsx`, not a standalone header click.
          openSectionAfterSelectionSync('type')
        }

        if (opts?.focusToolButton !== false) {
          setTimeout(() => typeToolButtonRef.current?.focus(), 0)
        }
      },
      focusSelectTool: () => {
        resetShapeCreation()
        activateTool('select')
        // Raw for the same reason as `expandAndOpenObject` above.
        openSectionAfterSelectionSync('object')
        // No `revealPanel()`: this runs after a delete, which leaves nothing selected, so it
        // mustn't reopen a panel the user collapsed. The Select button it focuses is on the
        // always-visible rail.

        // Deliberately does NOT dispatch 'select-tool-activated' — that event means "the user
        // pressed V to jump to the Select tool" (RightPanel/MenubarButton close in response).
        // This method is also called after deleting shapes (see LeftPanelRef doc comment) to
        // move focus off the Shapes tool button; firing the same event there closed the right
        // panel and any open menubar menu as an unrelated, unintended side effect of Delete.
        setTimeout(() => selectToolButtonRef.current?.focus(), 0)
      },
      openShapesPanel: () => {
        resetShapeCreation()
        activateTool('shape')
        revealPanel()
      },
      openSpellCheckPanel: () => {
        resetShapeCreation()
        activateTool('spellcheck')
        revealPanel()
      },
      handleRightPanelOpened: () => {
        // Opening a right panel always resets the Properties Panel to Select Tool, regardless of
        // which tool (Select/Type/Shape/Document) was active. Only the no-selection display
        // collapses; a selected object keeps its sections (WMPCM-11615 AC 3, see `LeftPanelRef`).
        resetShapeCreation()
        // Flagged for the effect above rather than collapsing inline: `activateTool('select')`
        // routes through `onToolActivate` (`home.tsx`'s `handleActivateTool`), which can itself
        // finalize an in-progress shape-text edit — that finalize's own commit/exit callbacks
        // run after this call returns, so collapsing here first left it able to reopen a section
        // behind this call. Deferring to an effect guarantees the collapse is the last word.
        pendingRightPanelCollapseRef.current = true
        // LeftPanel is memoized, and `activateTool('select')` changes nothing when Select is
        // already active, so force the commit that runs the effect. Otherwise the collapse waits
        // for an unrelated later render (e.g. a photo drop) and closes the sections it opens.
        requestDeferredWorkRender()
        activateTool('select')
      },
    }),
    [
      activeItem,
      activateTool,
      hidePanel,
      openSectionAfterSelectionSync,
      propSetSelectedShape,
      resetShapeCreation,
      revealPanel,
      selectToolButtonRef,
      typeToolButtonRef,
    ]
  )
}
